import {useRef} from 'react';
import type {PointerEvent} from 'react';

interface Axis {
  left: number;
  right: number;
  top: number;
  bottom: number;
  min: number;
  max: number;
}

interface ChartHandle {
  chartInstance: {
    canvas: HTMLCanvasElement;
    height: number;
    width: number;
    chartArea: {left: number; right: number; top: number; bottom: number};
    scales: Record<string, Axis>;
  };
}

interface DragAxis {
  index: number;
  axis: Axis;
}

export function useAxisDrag(onChange: (index: number, min: number, max: number) => void) {
  const chartRef = useRef<ChartHandle | null>(null);
  const suppressClick = useRef(false);
  const drag = useRef<{
    pointerId: number;
    startY: number;
    pixelsToChart: number;
    axes: DragAxis[];
  } | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || drag.current) return;
    suppressClick.current = false;
    const chart = chartRef.current?.chartInstance;
    if (!chart || event.target !== chart.canvas) return;
    const rect = chart.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = (event.clientX - rect.left) * chart.width / rect.width;
    const y = (event.clientY - rect.top) * chart.height / rect.height;
    const axes: DragAxis[] = [];
    ['y', 'y1', 'y2'].forEach((id, index) => {
      const axis = chart.scales[id];
      if (!axis || !Number.isFinite(axis.min) || !Number.isFinite(axis.max) || axis.max <= axis.min || axis.bottom <= axis.top) return;
      if (x >= axis.left && x <= axis.right && Math.abs(y - axis.top) <= 12) {
        axes.push({index, axis: {...axis}});
      }
    });
    if (!axes.length) return;
    drag.current = {pointerId: event.pointerId, startY: event.clientY, pixelsToChart: chart.height / rect.height, axes};
    event.currentTarget.setPointerCapture(event.pointerId);
    chart.canvas.style.cursor = 'ns-resize';
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current) {
      const chart = chartRef.current?.chartInstance;
      if (!chart) return;
      const rect = chart.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = (event.clientX - rect.left) * chart.width / rect.width;
      const y = (event.clientY - rect.top) * chart.height / rect.height;
      const overMaximum = ['y', 'y1', 'y2'].some((id) => {
        const axis = chart.scales[id];
        return axis && x >= axis.left && x <= axis.right && Math.abs(y - axis.top) <= 12;
      });
      chart.canvas.style.cursor = overMaximum ? 'ns-resize' : 'default';
      return;
    }
    if (!current || current.pointerId !== event.pointerId) return;
    const distance = event.clientY - current.startY;
    if (!suppressClick.current && Math.abs(distance) < 3) return;
    suppressClick.current = true;
    current.axes.forEach(({index, axis}) => {
      const span = axis.max - axis.min;
      // Up expands the range; down contracts it. Keep the lower bound fixed.
      const exponent = Math.max(-20, Math.min(20, -distance * current.pixelsToChart / (axis.bottom - axis.top)));
      const max = axis.min + span * Math.exp(exponent);
      const roundedMin = axis.min;
      const roundedMax = Number(max.toPrecision(8));
      if (Number.isFinite(roundedMin) && Number.isFinite(roundedMax) && roundedMin < roundedMax) onChange(index, roundedMin, roundedMax);
    });
  };

  const finish = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return {chartRef, suppressClick, handlers: {onPointerDown, onPointerMove, onPointerUp: finish, onPointerCancel: finish, onLostPointerCapture: finish}};
}
